export interface PromptCard {
  deckId: number;
  deckLabel: string;
  deckTitle: string;
  deckType: 'Phase' | 'Standalone';
  cardCategory: string;
  prompt: string;
  facilitationGuide: string;
}

export const PROMPT_CARDS: PromptCard[] = [
  // Phase 1 — Introductions
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Icebreaker',
    prompt: "What's one thing you hope to achieve from today's workshop?",
    facilitationGuide: 'Ask participants to share briefly. This helps set expectations and gives you insight into individual goals.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Background',
    prompt: "What's your role and how does it connect to today's challenge?",
    facilitationGuide: 'This helps everyone understand the diversity of perspectives in the room and begins to frame the challenge.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Experience',
    prompt: 'How long have you been working on problems related to this challenge?',
    facilitationGuide: 'Surfaces expertise in the room and helps identify who might have historical context.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Expectations',
    prompt: 'What would make today a success for you personally?',
    facilitationGuide: 'Helps align expectations and gives facilitators a chance to address any misalignments early.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Perspective',
    prompt: 'What unique perspective do you bring to this challenge?',
    facilitationGuide: 'Encourages participants to consider their unique value and primes them to contribute.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Connection',
    prompt: "How does this challenge connect to your team's broader goals?",
    facilitationGuide: 'Helps frame the workshop in the context of larger organizational objectives.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Concerns',
    prompt: "What's one concern you have about tackling this challenge?",
    facilitationGuide: 'Surfaces potential blockers early so they can be addressed throughout the workshop.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Previous Attempts',
    prompt: 'Has your team tried to solve this challenge before? What happened?',
    facilitationGuide: 'Uncovers historical context and prevents retreading unsuccessful approaches.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Stakeholders',
    prompt: "Who isn't in the room today that will be impacted by our decisions?",
    facilitationGuide: 'Helps identify missing perspectives and potential blind spots.',
  },
  {
    deckId: 1,
    deckLabel: 'Phase 1',
    deckTitle: 'Introductions',
    deckType: 'Phase',
    cardCategory: 'Ground Rules',
    prompt: "What's one workshop norm you'd like us to follow today?",
    facilitationGuide: 'Co-creates workshop guidelines and gives participants ownership of the process.',
  },
  // Phase 2 — Business Challenge
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Problem Definition',
    prompt: 'What problem are we really trying to solve?',
    facilitationGuide: 'Push for clarity and specificity. Avoid solutions disguised as problems.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Root Cause',
    prompt: 'What are the underlying causes of this challenge?',
    facilitationGuide: "Use the '5 Whys' technique to dig deeper beyond surface-level symptoms.",
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Impact',
    prompt: 'Who is most affected by this challenge and how?',
    facilitationGuide: 'Focus on specific stakeholders and concrete impacts to make the challenge tangible.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Metrics',
    prompt: 'How will we measure success in addressing this challenge?',
    facilitationGuide: 'Push for specific, measurable outcomes rather than vague improvements.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Scope',
    prompt: 'What aspects of this challenge are we NOT addressing today?',
    facilitationGuide: 'Setting boundaries helps focus the discussion and manages expectations.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'History',
    prompt: 'What previous attempts have been made to solve this challenge?',
    facilitationGuide: 'Understanding past efforts prevents repeating unsuccessful approaches.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Urgency',
    prompt: 'Why does this challenge need to be solved now?',
    facilitationGuide: 'Clarifies the timing and importance to ensure proper prioritization.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Stakeholders',
    prompt: 'Who are all the stakeholders affected by this challenge?',
    facilitationGuide: 'Create a comprehensive list to ensure all perspectives are considered.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Constraints',
    prompt: 'What constraints must any solution work within?',
    facilitationGuide: 'Identify technical, budget, timeline, or policy constraints early.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Reframing',
    prompt: "How might we restate this challenge from the user's perspective?",
    facilitationGuide: 'Shifting perspective often reveals new insights about the real problem.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Business Goals',
    prompt: 'How does solving this challenge support broader business objectives?',
    facilitationGuide: 'Connect the challenge to strategic priorities to ensure alignment.',
  },
  {
    deckId: 2,
    deckLabel: 'Phase 2',
    deckTitle: 'Business Challenge',
    deckType: 'Phase',
    cardCategory: 'Challenge Statement',
    prompt: 'Can we craft a single sentence that captures the essence of our challenge?',
    facilitationGuide: 'Work together to create a concise statement that will guide the rest of the workshop.',
  },
  // Phase 3 — Future Framing
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Future State',
    prompt: "Imagine it's one year from now and we've successfully addressed this challenge. What headline would announce our success?",
    facilitationGuide: 'Encourage bold, specific thinking about meaningful outcomes rather than features.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Press Release',
    prompt: 'Draft the first paragraph of a press release announcing our successful solution.',
    facilitationGuide: 'Focus on the value delivered and impact created, not the technical details.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Customer Quote',
    prompt: 'Write a quote from a delighted customer about how our solution changed their experience.',
    facilitationGuide: 'Make it specific and emotional - what real difference did it make?',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Executive Quote',
    prompt: 'Draft a quote from a company executive about why this solution matters to the business.',
    facilitationGuide: 'Connect to strategic priorities and business outcomes.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Key Features',
    prompt: 'What are the 3-5 most important capabilities of our solution?',
    facilitationGuide: 'Focus on value-delivering capabilities, not technical specifications.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'FAQ: Differentiation',
    prompt: 'How will customers ask us to compare this to existing alternatives?',
    facilitationGuide: "Anticipate comparison questions and how we'd articulate unique value.",
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'FAQ: Concerns',
    prompt: 'What concerns or objections might customers raise?',
    facilitationGuide: 'Identify potential barriers to adoption or points of resistance.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'FAQ: Requirements',
    prompt: 'What will customers need to have in place to use this solution?',
    facilitationGuide: 'Consider technical, process, or organizational prerequisites.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Internal FAQ',
    prompt: 'What questions will our own team have about building and supporting this solution?',
    facilitationGuide: 'Surface internal concerns about feasibility, resources, or capabilities.',
  },
  {
    deckId: 3,
    deckLabel: 'Phase 3',
    deckTitle: 'Future Framing',
    deckType: 'Phase',
    cardCategory: 'Success Metrics',
    prompt: 'How will we measure the success of this solution after launch?',
    facilitationGuide: 'Define specific, measurable outcomes that indicate real success.',
  },
  // Phase 4 — User Personas
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'Primary User',
    prompt: 'Who is the primary user who will benefit from our solution?',
    facilitationGuide: 'Be specific about role, context, and relationship to the challenge.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Demographics',
    prompt: 'What are the key demographic characteristics of our primary user?',
    facilitationGuide: 'Consider age, location, education, technical proficiency, etc.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Goals',
    prompt: 'What is our primary user trying to accomplish?',
    facilitationGuide: 'Focus on their fundamental goals, not just their interaction with our solution.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Pain Points',
    prompt: 'What frustrates our user most about their current experience?',
    facilitationGuide: 'Identify emotional pain points as well as functional ones.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Context',
    prompt: 'In what environment or context does our user encounter this challenge?',
    facilitationGuide: 'Consider physical location, devices used, time constraints, etc.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Motivations',
    prompt: 'What motivates our user? What do they care about most?',
    facilitationGuide: 'Understand deeper drivers beyond the immediate task.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Behaviors',
    prompt: 'What behaviors or workarounds has our user developed to deal with this challenge?',
    facilitationGuide: 'Existing adaptations often reveal important insights about needs.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'Secondary Users',
    prompt: 'Who else interacts with or is affected by our solution?',
    facilitationGuide: 'Consider all stakeholders in the ecosystem around the primary user.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Quotes',
    prompt: 'What might our user say about their current experience?',
    facilitationGuide: 'Create realistic quotes that capture their frustration or needs.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Expertise',
    prompt: 'What level of expertise does our user have with this type of solution?',
    facilitationGuide: 'Consider their familiarity with similar tools or processes.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'User Constraints',
    prompt: 'What limitations or constraints affect how our user would adopt a solution?',
    facilitationGuide: 'Consider time, budget, authority, technical capabilities, etc.',
  },
  {
    deckId: 4,
    deckLabel: 'Phase 4',
    deckTitle: 'User Personas',
    deckType: 'Phase',
    cardCategory: 'Persona Synthesis',
    prompt: 'Can we create a name and brief description for our primary user persona?',
    facilitationGuide: 'Synthesize insights into a memorable character that the team can reference.',
  },
  // Phase 5 — User Journeys
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Journey Start',
    prompt: "What triggers the start of our user's journey?",
    facilitationGuide: 'Identify the specific event or need that begins the experience.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Journey End',
    prompt: "What defines a successful end to our user's journey?",
    facilitationGuide: 'Be clear about the outcome that represents success for the user.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Key Steps',
    prompt: "What are the main steps in our user's current journey?",
    facilitationGuide: 'Map out 5-7 high-level stages from beginning to end.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Pain Points',
    prompt: 'Where does our user experience the most friction or frustration?',
    facilitationGuide: 'Identify specific moments of difficulty in the current journey.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Touchpoints',
    prompt: 'What systems, people, or channels does our user interact with?',
    facilitationGuide: 'Map all touchpoints across the entire journey.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Emotions',
    prompt: 'How does our user feel at each stage of their journey?',
    facilitationGuide: 'Track emotional highs and lows throughout the experience.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Decisions',
    prompt: 'What key decisions does our user make during their journey?',
    facilitationGuide: 'Identify decision points and what information they need.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Backstage Processes',
    prompt: "What happens behind the scenes that affects our user's journey?",
    facilitationGuide: 'Map internal processes that impact the user experience.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Time Factors',
    prompt: 'How long does each stage of the journey take?',
    facilitationGuide: 'Identify time-consuming steps that might be optimized.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Opportunity Areas',
    prompt: 'Where in the journey could we create the most value for our user?',
    facilitationGuide: 'Identify high-impact intervention points.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Ideal Journey',
    prompt: 'What would an ideal future journey look like for our user?',
    facilitationGuide: 'Reimagine the journey with pain points removed and new value added.',
  },
  {
    deckId: 5,
    deckLabel: 'Phase 5',
    deckTitle: 'User Journeys',
    deckType: 'Phase',
    cardCategory: 'Journey Metrics',
    prompt: 'How will we measure improvements to the user journey?',
    facilitationGuide: 'Define specific metrics for each stage of the journey.',
  },
  // Phase 6 — Assumptions & Constraints
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'User Assumptions',
    prompt: 'What are we assuming about our users that might not be true?',
    facilitationGuide: 'Challenge assumptions about needs, behaviors, and preferences.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Technical Assumptions',
    prompt: 'What are we assuming about technical feasibility?',
    facilitationGuide: 'Surface assumptions about what can be built and integrated.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Business Assumptions',
    prompt: 'What are we assuming about the business model or value proposition?',
    facilitationGuide: 'Question assumptions about how this creates business value.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Market Assumptions',
    prompt: 'What are we assuming about the market or competitive landscape?',
    facilitationGuide: 'Challenge assumptions about market readiness and competition.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Resource Constraints',
    prompt: 'What resource limitations must we work within?',
    facilitationGuide: 'Identify constraints around budget, people, and time.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Technical Constraints',
    prompt: 'What technical constraints will affect our solution?',
    facilitationGuide: 'Consider platform limitations, integration requirements, etc.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Policy Constraints',
    prompt: 'What policy, legal, or compliance constraints must we consider?',
    facilitationGuide: 'Identify regulatory requirements or internal policies.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Constraint Creativity',
    prompt: 'How might our constraints actually drive innovation?',
    facilitationGuide: 'Reframe constraints as creative boundaries that can spark new thinking.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Assumption Prioritization',
    prompt: 'Which assumptions are most critical to validate first?',
    facilitationGuide: 'Prioritize based on risk level and impact on solution direction.',
  },
  {
    deckId: 6,
    deckLabel: 'Phase 6',
    deckTitle: 'Assumptions & Constraints',
    deckType: 'Phase',
    cardCategory: 'Constraint Negotiation',
    prompt: 'Which constraints might be flexible or negotiable?',
    facilitationGuide: 'Identify constraints that could potentially be changed or removed.',
  },
  // Phase 7 — Next Steps
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Key Decisions',
    prompt: 'What key decisions have we made today?',
    facilitationGuide: 'Document the major decisions and direction set during the workshop.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Open Questions',
    prompt: 'What important questions remain unanswered?',
    facilitationGuide: 'Capture questions that need further research or discussion.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Action Items',
    prompt: 'What specific actions need to be taken after this workshop?',
    facilitationGuide: 'Create clear, assignable tasks with owners and deadlines.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Timeline',
    prompt: 'What are the key milestones and when should they be completed?',
    facilitationGuide: 'Create a high-level timeline with major checkpoints.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Success Metrics',
    prompt: 'How will we measure the success of our next steps?',
    facilitationGuide: 'Define clear metrics for the immediate next phase of work.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Risk Mitigation',
    prompt: 'What risks should we plan to address?',
    facilitationGuide: 'Identify potential obstacles and mitigation strategies.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Decision Framework',
    prompt: 'How will we make decisions as we move forward?',
    facilitationGuide: 'Establish a clear process for making decisions during execution.',
  },
  {
    deckId: 7,
    deckLabel: 'Phase 7',
    deckTitle: 'Next Steps',
    deckType: 'Phase',
    cardCategory: 'Follow-up Meeting',
    prompt: 'When should we reconvene to check on progress?',
    facilitationGuide: 'Schedule the next checkpoint to maintain momentum.',
  },
  // Deck 8 — Challenge Reframe (Standalone)
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Problem Expansion',
    prompt: 'How might we expand the scope of our challenge to see the bigger picture?',
    facilitationGuide: 'Push the team to zoom out and consider broader context and implications.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Alternative Perspectives',
    prompt: 'How would different stakeholders define this challenge?',
    facilitationGuide: 'Consider the perspective of users, executives, partners, etc.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'First Principles',
    prompt: 'What are the fundamental truths or principles at the core of this challenge?',
    facilitationGuide: 'Break down the challenge to its most basic elements.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Opposite Day',
    prompt: 'What if the opposite of our current framing is true?',
    facilitationGuide: 'Deliberately invert assumptions to reveal new insights.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Future Perspective',
    prompt: 'How might this challenge look different in 5 years?',
    facilitationGuide: 'Consider how trends and changes might affect the nature of the problem.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Metaphor Shift',
    prompt: 'What metaphor or analogy might help us see this challenge differently?',
    facilitationGuide: 'Use metaphorical thinking to reveal new aspects of the challenge.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Challenge Hierarchy',
    prompt: 'Is this a symptom of a deeper challenge we should be addressing?',
    facilitationGuide: 'Identify whether we\'re addressing root causes or just symptoms.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Constraint Removal',
    prompt: "What if our biggest constraint didn't exist?",
    facilitationGuide: 'Imagine solving the challenge without a key limitation.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Adjacent Possibilities',
    prompt: 'What related challenges might we solve that would make this one easier?',
    facilitationGuide: 'Look for connected problems that might offer leverage.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Extreme Users',
    prompt: 'How would extreme or edge case users experience this challenge?',
    facilitationGuide: 'Consider outlier perspectives to reveal overlooked aspects.',
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Historical Context',
    prompt: 'How has this challenge evolved over time?',
    facilitationGuide: "Look at the history to understand how we got here and what's changed.",
  },
  {
    deckId: 8,
    deckLabel: 'Challenge Reframe',
    deckTitle: 'Challenge Reframe',
    deckType: 'Standalone',
    cardCategory: 'Challenge Restatement',
    prompt: 'Can we restate the challenge in at least three different ways?',
    facilitationGuide: 'Create multiple alternative framings and evaluate which is most productive.',
  },
  // Deck 9 — Assumption Buster (Standalone)
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Solution Questioning',
    prompt: 'What if our current solution approach is completely wrong?',
    facilitationGuide: 'Challenge the team to consider radically different approaches.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Evidence Check',
    prompt: 'What evidence do we have that supports our current assumptions?',
    facilitationGuide: 'Distinguish between facts, beliefs, and assumptions.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Reverse Assumptions',
    prompt: 'What if the opposite of each of our key assumptions is true?',
    facilitationGuide: 'Systematically invert assumptions to reveal blind spots.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Worst Case Scenario',
    prompt: "What's the worst that could happen if we're wrong about our assumptions?",
    facilitationGuide: 'Evaluate downside risks to prioritize which assumptions to test.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Industry Transplant',
    prompt: 'How would a company in a completely different industry approach this challenge?',
    facilitationGuide: 'Borrow perspectives from unrelated fields to challenge conventional thinking.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Time Travel',
    prompt: 'How would we solve this differently if we were in the past or future?',
    facilitationGuide: 'Use temporal shifts to reveal assumptions tied to current context.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Competitor Perspective',
    prompt: 'How might our biggest competitor approach this challenge?',
    facilitationGuide: 'Adopt a different organizational perspective to reveal biases.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Minimum Viable Test',
    prompt: "What's the smallest experiment we could run to test our riskiest assumption?",
    facilitationGuide: 'Design quick, low-cost ways to validate or invalidate key assumptions.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Pre-mortem',
    prompt: 'Imagine our solution has failed. What caused it to fail?',
    facilitationGuide: 'Work backwards from failure to identify hidden assumptions and risks.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Expert Challenge',
    prompt: "What would an expert in this field say we're missing?",
    facilitationGuide: 'Adopt the perspective of deep domain expertise to find blind spots.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: 'Assumption Mapping',
    prompt: 'Which of our assumptions are high-risk and high-impact?',
    facilitationGuide: 'Plot assumptions on a risk/impact matrix to prioritize testing.',
  },
  {
    deckId: 9,
    deckLabel: 'Assumption Buster',
    deckTitle: 'Assumption Buster',
    deckType: 'Standalone',
    cardCategory: "Devil's Advocate",
    prompt: "What's the strongest case against our current approach?",
    facilitationGuide: 'Deliberately argue against the prevailing direction to test its strength.',
  },
];

// Helper to get cards by deck ID
export function getCardsByDeck(deckId: number): PromptCard[] {
  return PROMPT_CARDS.filter((c) => c.deckId === deckId);
}

// Helper to get cards by phase index (0-based, maps to deck IDs 1-7)
export function getCardsByPhase(phaseIndex: number): PromptCard[] {
  return PROMPT_CARDS.filter((c) => c.deckId === phaseIndex + 1 && c.deckType === 'Phase');
}

// Get standalone decks
export function getStandaloneCards(): PromptCard[] {
  return PROMPT_CARDS.filter((c) => c.deckType === 'Standalone');
}
